import React, { useState } from 'react';
import { ScrollArea } from '@/lib/components/ui/scroll-area';
import { Button } from '@/lib/components/ui/button';
import { ArrowRight, Save } from 'lucide-react';
import { AttributeType } from './AbacSidebar';
import { RolesContent } from './AbacContent/RolesContent';
import { WeekDaysContent } from './AbacContent/WeekDaysContent';
import { MonthDaysContent } from './AbacContent/MonthDaysContent';
import { HoursContent } from './AbacContent/HoursContent';
import { PermissionsView } from './AbacContent/PermissionsView';
import { TAbac } from '@/data/types';

interface AbacContentProps {
  selectedAttribute: AttributeType;
  onSelectionChange: (attribute: AttributeType, selections: any) => void;
  currentSelections: TAbac
  onActionChange: (action: 'READ' | 'UPDATE' | 'DELETE' | undefined) => void;
  onPrivateChange: (isPrivate: boolean) => void;
  viewMode: 'collection' | 'editor' | 'permissions';
  onSetPermissions: () => void;
  onSavePermissions?: (name: string, description?: string) => void;
}

export const AbacContent: React.FC<AbacContentProps> = ({
  selectedAttribute,
  onSelectionChange,
  currentSelections,
  onActionChange,
  onPrivateChange,
  viewMode,
  onSetPermissions,
  onSavePermissions,
}) => {
  // Use props for state instead of local state
  const selectedRoles = new Set(currentSelections.roles);
  const selectedWeekDays = new Set(currentSelections.week_days);
  const selectedMonthDays = new Set(currentSelections.month_days);
  const selectedHours = new Set(currentSelections.hours?.toString());
  const selectedAction = currentSelections.action;
  const isPrivate = currentSelections.private || false;

 



  // Handlers for 'all-together' mode
  const handleRoleToggle = (roleId: string) => {
    const newSelected = new Set(selectedRoles);
    if (newSelected.has(roleId)) {
      newSelected.delete(roleId);
    } else {
      newSelected.add(roleId);
    }
    onSelectionChange('roles', Array.from(newSelected));
  };

  const handleWeekDayToggle = (day: number) => {
    const newSelected = new Set(selectedWeekDays);
    if (newSelected.has(day)) {
      newSelected.delete(day);
    } else {
      newSelected.add(day);
    }
    onSelectionChange('week_days', Array.from(newSelected));
  };

  const handleMonthDayToggle = (day: number) => {
    const newSelected = new Set(selectedMonthDays);
    if (newSelected.has(day)) {
      newSelected.delete(day);
    } else {
      newSelected.add(day);
    }
    onSelectionChange('month_days', Array.from(newSelected));
  };

  const handleHourRangeToggle = (hourId: string) => {
    const newSelected = new Set(selectedHours);
    if (newSelected.has(hourId)) {
      newSelected.delete(hourId);
    } else {
      newSelected.add(hourId);
    }
    onSelectionChange('hours', Array.from(newSelected));
  };

  const handleActionSelect = (action: 'READ' | 'UPDATE' | 'DELETE') => {
    const newAction = selectedAction === action ? undefined : action;
    onActionChange(newAction);
  };

  const renderContent = () => {
    if (viewMode === 'permissions') {
      return (
        <PermissionsView
          currentSelections={currentSelections}
          onActionChange={onActionChange}
          onPrivateChange={onPrivateChange}
          onSavePermissions={onSavePermissions || (() => {})}
        />
      );
    }

    switch (selectedAttribute) {
      case 'roles':
        return <RolesContent selectedRoles={selectedRoles} onRoleToggle={handleRoleToggle} onRoleClick={undefined} />;
      case 'week_days':
        return (
          <WeekDaysContent
            selectedWeekDays={selectedWeekDays}
            onWeekDayToggle={handleWeekDayToggle}
            onWeekDayClick={undefined}
          />
        );
      case 'month_days':
        return (
          <MonthDaysContent
            selectedMonthDays={selectedMonthDays}
            onMonthDayToggle={handleMonthDayToggle}
            onMonthDayClick={undefined}
          />
        );
      case 'hours':
        return (
          <HoursContent
            selectedHours={selectedHours}
            onHourRangeToggle={handleHourRangeToggle}
            onHourRangeClick={undefined}
            selectedAction={selectedAction}
            onActionSelect={handleActionSelect}
            isPrivate={isPrivate}
            onPrivateToggle={onPrivateChange}
          />
        );
      default:
        return (
          <div className="flex h-full items-center justify-center p-6">
            <div className="animate-fade-in animation-delay-200 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 animate-bounce items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600"></div>
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-900">Select an Attribute</h2>
              <p className="text-gray-600">Choose an attribute from the sidebar to configure permissions</p>
            </div>
          </div>
        );
    }
  };

  // Check if we have any selections to show the next button
  const hasAnySelections =
    (currentSelections.roles || [])?.length > 0 ||
    (currentSelections.week_days || [])?.length > 0 ||
    (currentSelections.month_days || [])?.length > 0 ||
    (currentSelections.hours || [])?.length > 0;

  return (
    <div className="animate-fade-in animation-delay-100 relative flex h-full flex-1 flex-col">
      <ScrollArea className="h-full">{renderContent()}</ScrollArea>

      {/* Absolute positioned arrow button for editor mode */}
      {viewMode === 'editor' && hasAnySelections && (
        <Button
          onClick={onSetPermissions}
          className="absolute right-6 bottom-6 h-14 w-14 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 p-0 text-white shadow-lg transition-all duration-200 hover:scale-110 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
        >
          <ArrowRight className="h-6 w-6" />
        </Button>
      )}

      {/* Absolute positioned save button for permissions mode */}
      {viewMode === 'permissions' && hasAnySelections && currentSelections.action && (
        <Button
          onClick={() => onSavePermissions?.('Permission Collection', '')}
          className="absolute right-6 bottom-6 h-16 w-16 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 p-0 text-white shadow-lg transition-all duration-200 hover:scale-110 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl"
        >
          <Save className="h-7 w-7" />
        </Button>
      )}
    </div>
  );
};
